import React, { createContext, useCallback, useContext, useState } from "react";

const ConfirmContext = createContext({ confirm: async () => false });

// Drop-in replacement for window.confirm() that matches the dark-gold theme.
// Usage:
//   const { confirm } = useConfirm();
//   if (!await confirm({ title: "Delete?", message: "This cannot be undone." })) return;
export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise(resolve => {
      setState({
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        danger: !!opts.danger,
        resolve,
      });
    });
  }, []);

  const done = (val) => {
    if (state?.resolve) state.resolve(val);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <div
          data-testid="confirm-dialog"
          role="dialog"
          aria-modal="true"
          onClick={() => done(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.74)",
            zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 18,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-card"
            style={{
              maxWidth: 440, width: "100%", padding: 30,
              border: "1px solid " + (state.danger ? "rgba(220,80,60,0.4)" : "rgba(212,175,55,0.35)"),
            }}
          >
            <div className="serif" style={{ fontSize: 24, color: "#fff", marginBottom: 10, fontWeight: 400 }}>
              {state.title}
            </div>
            {state.message && (
              <p style={{ color: "#cfcfcf", fontSize: 14, lineHeight: 1.6, marginBottom: 22 }}>
                {state.message}
              </p>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                data-testid="confirm-cancel"
                onClick={() => done(false)}
                className="btn-ghost"
                style={{ padding: "11px 22px", minWidth: 110 }}
              >
                {state.cancelLabel}
              </button>
              <button
                data-testid="confirm-confirm"
                onClick={() => done(true)}
                className={state.danger ? "" : "btn-gold"}
                style={{
                  padding: "11px 22px", minWidth: 110, cursor: "pointer",
                  fontWeight: 600, borderRadius: 8,
                  ...(state.danger ? {
                    background: "linear-gradient(180deg, #c84a3a 0%, #8a2a1c 100%)",
                    color: "#fff",
                    border: "1px solid #c84a3a",
                  } : {}),
                }}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
